import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "mb_cookie_consent";

const readConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const applyConsent = (consent) => {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    ad_storage: consent.ads ? "granted" : "denied",
    ad_user_data: consent.ads ? "granted" : "denied",
    ad_personalization: consent.ads ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
  });
};

const Toggle = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 p-4">
    <div>
      <p className="text-sm font-semibold text-white">{label}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{desc}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${checked ? "bg-gold" : "bg-slate-600"}`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all duration-200 ${checked ? "left-6" : "left-1"}`}
      />
    </button>
  </div>
);

const CookieConsent = () => {
  const [visible, setVisible] = useState(() => !readConsent());
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState({ ads: false, analytics: false });

  const save = useCallback((consent) => {
    const value = { ...consent, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    applyConsent(value);
    setVisible(false);
  }, []);

  useEffect(() => {
    const open = () => {
      const current = readConsent();
      setPrefs({ ads: !!current?.ads, analytics: !!current?.analytics });
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-preferences", open);
    return () => window.removeEventListener("open-cookie-preferences", open);
  }, []);

  if (!visible) return null;

  return (
    <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-40 p-3 sm:p-5">
      <div className="mx-auto max-w-3xl rounded-2xl border border-gold/30 bg-navy p-5 text-white shadow-2xl sm:p-6">
        <div className="flex items-start gap-3">
          <Cookie size={22} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
          <div className="min-w-0">
            <p className="font-serif text-lg font-semibold">Rispettiamo la tua privacy</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-300">
              Questo sito utilizza cookie tecnici necessari al funzionamento e, solo con il
              tuo consenso, strumenti di misurazione e pubblicità di Google. Puoi
              accettare, rifiutare o personalizzare le preferenze, e modificarle in
              qualsiasi momento dalla pagina{" "}
              <Link to="/cookie-policy" data-testid="cookie-banner-policy-link" className="font-semibold text-gold-light underline underline-offset-2">
                Cookie Policy
              </Link>
              .
            </p>
          </div>
        </div>

        {customizing && (
          <div data-testid="cookie-preferences-panel" className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 p-4 opacity-80">
              <div>
                <p className="text-sm font-semibold text-white">Cookie tecnici</p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-400">
                  Necessari al funzionamento del sito. Sempre attivi.
                </p>
              </div>
              <span className="rounded-full bg-gold/20 px-3 py-1 text-xs font-semibold text-gold-light">
                Sempre attivi
              </span>
            </div>
            <Toggle
              label="Misurazione e pubblicità (Google Ads)"
              desc="Consentono a Google di misurare l'efficacia degli annunci. Segnali: ad_storage, ad_user_data, ad_personalization."
              checked={prefs.ads}
              onChange={(v) => setPrefs((p) => ({ ...p, ads: v }))}
              testId="cookie-toggle-ads"
            />
            <Toggle
              label="Statistiche"
              desc="Eventuali strumenti di analisi statistica delle visite. Segnale: analytics_storage."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testId="cookie-toggle-analytics"
            />
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          {customizing ? (
            <button
              type="button"
              onClick={() => save(prefs)}
              data-testid="cookie-save-preferences-button"
              className="btn-gold !px-5 !py-2.5 text-sm"
            >
              Salva le preferenze
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => save({ ads: true, analytics: true })}
                data-testid="cookie-accept-button"
                className="btn-gold !px-5 !py-2.5 text-sm"
              >
                Accetta tutti
              </button>
              <button
                type="button"
                onClick={() => save({ ads: false, analytics: false })}
                data-testid="cookie-reject-button"
                className="rounded-full border border-white/30 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold-light"
              >
                Rifiuta
              </button>
              <button
                type="button"
                onClick={() => setCustomizing(true)}
                data-testid="cookie-customize-button"
                className="px-3 py-2.5 text-sm font-semibold text-slate-300 underline underline-offset-2 transition-colors hover:text-gold-light"
              >
                Personalizza
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
